import React, { useState, useEffect, useMemo, useRef } from 'react';
import { clasesService, tareasService, cursosService } from '../services/api';
import './Calendario.css';

const DIA_SEMANA = ['Dom', 'Lun', 'Mar', 'Miér', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const parseFechaLocal = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  const onlyDate = s.slice(0, 10);
  const m = onlyDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    return new Date(y, mo - 1, d);
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const formatHora = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const formatHoraDesdeTime = (timeStr) => {
  if (!timeStr) return '';
  if (typeof timeStr === 'string' && timeStr.length >= 5) return timeStr.substring(0, 5);
  return String(timeStr);
};

const formatFecha = (dateStr) => {
  if (!dateStr) return '';
  const d = parseFechaLocal(dateStr);
  return d ? d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

const GRUPOS_EVENTO_CALENDARIO = [
  { type: 'cursada', titulo: 'Reunión / Cursada' },
  { type: 'publicacion_clase', titulo: 'Publicación de clase' },
  { type: 'tarea', titulo: 'Tareas' },
];

function agruparEventosPorTipo(eventos) {
  const porTipo = {};
  (eventos || []).forEach((ev) => {
    if (!porTipo[ev.type]) porTipo[ev.type] = [];
    porTipo[ev.type].push(ev);
  });
  return GRUPOS_EVENTO_CALENDARIO
    .filter((g) => (porTipo[g.type] || []).length > 0)
    .map((g) => ({ ...g, items: porTipo[g.type] }));
}

/** Hora límite de entrega en 24 h: si el backend solo envía fecha (YYYY-MM-DD), asumimos fin del día 23:59. */
const formatHoraCierreTarea = (fechaCierre) => {
  if (!fechaCierre) return '';
  const s = String(fechaCierre).trim();
  const datePart = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return '23:59';
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '23:59';
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
};

/** Fechas de clase del curso (reuniones Meet/Zoom = asistencia). Misma lógica que backend: 0=Lun, 6=Dom */
function fechasClaseCurso(curso) {
  const inicio = curso.fecha_inicio ? parseFechaLocal(curso.fecha_inicio) : null;
  const fin = curso.fecha_fin ? parseFechaLocal(curso.fecha_fin) : null;
  if (!inicio || !fin) return [];
  let diasSemana = curso.dias_semana;
  if (Array.isArray(diasSemana) && diasSemana.length === 0) diasSemana = null;
  if (curso.clases_por_semana === 1 && inicio) {
    const pyWeekday = (inicio.getDay() + 6) % 7;
    diasSemana = [pyWeekday];
  }
  if (!diasSemana || !Array.isArray(diasSemana)) return [];
  const fechas = [];
  const d = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
  const end = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate());
  while (d <= end) {
    const pyWeekday = (d.getDay() + 6) % 7;
    if (diasSemana.includes(pyWeekday)) {
      fechas.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return fechas;
}

/** Paleta de colores para distinguir cursos (solo cuando colorearCursadasPorCurso) */
const PALETA_CURSOS = [
  '#2563eb', '#059669', '#b45309', '#7c3aed', '#0d9488',
  '#dc2626', '#ca8a04', '#db2777', '#0891b2', '#65a30d',
];

/**
 * @param {Object} props
 * @param {'estudiante'|'docente'} [props.cursosSource] - 'estudiante' = mis cursos + contenido acotado; 'docente' = getCursos + listas globales filtradas
 * @param {boolean} [props.soloCursadas] - Si true, solo muestra fechas de cursada/reunión (no publicaciones ni tareas)
 * @param {string} [props.titulo] - Título del calendario (por defecto "Calendario")
 * @param {boolean} [props.colorearCursadasPorCurso] - Si true, cada curso tiene un color en cursadas (solo Reuniones; no afecta vista estudiante)
 * @param {boolean} [props.vistaAnioCompleto] - Si true, muestra los 12 meses del año en vista compacta (solo Reuniones)
 * @param {Array} [props.reunionesData] - Si se pasa (desde API reuniones), se usan estas fechas en lugar de calcular desde curso (solo docente)
 * @param {function} [props.onDayClick] - (dateKey, eventos) al hacer clic en un día (para editar reuniones)
 */
const Calendario = ({ cursosSource = 'estudiante', soloCursadas = false, titulo = 'Calendario', colorearCursadasPorCurso = false, vistaAnioCompleto = false, reunionesData = null, onDayClick }) => {
  const [cursos, setCursos] = useState([]);
  const [clases, setClases] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [tooltip, setTooltip] = useState(null);
  const cuerpoRef = useRef(null);
  const panelEventosScrollRef = useRef(null);

  useEffect(() => {
    if (panelEventosScrollRef.current) {
      panelEventosScrollRef.current.scrollTop = 0;
    }
  }, [tooltip?.key]);

  useEffect(() => {
    const cuerpo = cuerpoRef.current;
    if (!cuerpo) return undefined;

    const onWheel = (e) => {
      if (!tooltip) return;
      const scrollEl = panelEventosScrollRef.current;
      if (!scrollEl || scrollEl.scrollHeight <= scrollEl.clientHeight) return;
      if (!e.target.closest?.('.calendario-dia-con-eventos')) return;

      e.preventDefault();
      e.stopPropagation();
      scrollEl.scrollTop += e.deltaY;
    };

    cuerpo.addEventListener('wheel', onWheel, { passive: false });
    return () => cuerpo.removeEventListener('wheel', onWheel);
  }, [tooltip]);

  const load = React.useCallback(async () => {
    if (reunionesData !== null && reunionesData !== undefined) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (cursosSource === 'docente') {
        const promises = [cursosService.getCursos().catch(() => [])];
        if (!soloCursadas) {
          promises.push(clasesService.getClases(), tareasService.getTareas());
        } else {
          promises.push(Promise.resolve([]), Promise.resolve([]));
        }
        const [cursosData, clasesData, tareasData] = await Promise.all(promises);
        const misList = Array.isArray(cursosData) ? cursosData : [];
        const misCursoIds = new Set(misList.map((c) => c.id).filter(Boolean));
        setCursos(misList);
        const todasClases = Array.isArray(clasesData) ? clasesData : [];
        const todasTareas = Array.isArray(tareasData) ? tareasData : [];
        setClases(todasClases.filter((c) => c.curso != null && misCursoIds.has(c.curso)));
        setTareas(todasTareas.filter((t) => t.curso != null && misCursoIds.has(t.curso)));
      } else {
        const [cursosData, contenido] = await Promise.all([
          cursosService.getMisCursos().catch(() => []),
          soloCursadas
            ? Promise.resolve(null)
            : cursosService.getMisCursosContenido().catch(() => ({ clases: [], tareas: [] })),
        ]);
        setCursos(Array.isArray(cursosData) ? cursosData : []);
        if (soloCursadas || !contenido) {
          setClases([]);
          setTareas([]);
        } else {
          setClases(Array.isArray(contenido.clases) ? contenido.clases : []);
          setTareas(Array.isArray(contenido.tareas) ? contenido.tareas : []);
        }
      }
    } catch {
      setCursos([]);
      setClases([]);
      setTareas([]);
    } finally {
      setLoading(false);
    }
  }, [cursosSource, soloCursadas, reunionesData]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (reunionesData !== null && reunionesData !== undefined) {
      setLoading(false);
    }
  }, [reunionesData]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener('calendarRefresh', onRefresh);
    return () => window.removeEventListener('calendarRefresh', onRefresh);
  }, [load]);

  const eventosPorFecha = useMemo(() => {
    const map = {};
    const add = (key, ev) => {
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    };

    if (reunionesData != null && Array.isArray(reunionesData)) {
      reunionesData.forEach((curso) => {
        const reuniones = curso.reuniones || [];
        reuniones.forEach((r) => {
          const fechaStr = r.fecha;
          if (!fechaStr) return;
          const key = fechaStr.length >= 10 ? fechaStr.substring(0, 10).replace(/-/g, '-') : fechaStr;
          const ev = {
            type: 'cursada',
            curso_nombre: curso.nombre || '',
            hora: formatHoraDesdeTime(r.hora),
          };
          if (colorearCursadasPorCurso && curso.id != null) ev.curso_id = curso.id;
          add(key, ev);
        });
      });
    } else {
      cursos.forEach((curso) => {
        const reunionesEfectivas = Array.isArray(curso.reuniones) ? curso.reuniones : null;
        if (reunionesEfectivas && reunionesEfectivas.length > 0) {
          reunionesEfectivas.forEach((r) => {
            const fechaStr = r.fecha;
            if (!fechaStr) return;
            const key = fechaStr.length >= 10 ? fechaStr.substring(0, 10) : fechaStr;
            const ev = {
              type: 'cursada',
              curso_nombre: curso.nombre || '',
              hora: formatHoraDesdeTime(r.hora),
            };
            if (colorearCursadasPorCurso && curso.id != null) ev.curso_id = curso.id;
            add(key, ev);
          });
        } else {
          fechasClaseCurso(curso).forEach((fecha) => {
            const key = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`;
            const ev = {
              type: 'cursada',
              curso_nombre: curso.nombre || '',
              hora: formatHoraDesdeTime(curso.hora_inicio_clase),
            };
            if (colorearCursadasPorCurso && curso.id != null) ev.curso_id = curso.id;
            add(key, ev);
          });
        }
      });
    }

    clases.forEach((c) => {
      const fechaValor = c.fecha_publicacion || c.fecha_programada;
      if (fechaValor) {
        const d = new Date(fechaValor);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        add(key, {
          type: 'publicacion_clase',
          titulo: c.titulo || 'Clase',
          curso_nombre: c.curso_nombre || '',
          fecha_publicacion: c.fecha_publicacion,
        });
      }
    });

    tareas.forEach((t) => {
      if (t.fecha_cierre) {
        const s = String(t.fecha_cierre).trim().slice(0, 10);
        const [y, m, day] = s.split('-').map(Number);
        const d = (y && m && day) ? new Date(y, m - 1, day) : new Date(t.fecha_cierre);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        add(key, {
          type: 'tarea',
          titulo: t.titulo || 'Tarea',
          curso_nombre: t.curso_nombre || '',
          fecha_cierre: t.fecha_cierre,
          hora_cierre: formatHoraCierreTarea(t.fecha_cierre),
          created_at: t.created_at,
        });
      }
    });
    return map;
  }, [cursos, clases, tareas, colorearCursadasPorCurso, reunionesData]);

  const { paletteByCursoId, cursosOrdenados } = useMemo(() => {
    const source = (reunionesData != null && Array.isArray(reunionesData)) ? reunionesData : cursos;
    if (!colorearCursadasPorCurso || !source.length) return { paletteByCursoId: new Map(), cursosOrdenados: [] };
    const ordenados = [...source].filter((c) => c.id != null).sort((a, b) => a.id - b.id);
    const map = new Map();
    ordenados.forEach((c, i) => {
      map.set(c.id, PALETA_CURSOS[i % PALETA_CURSOS.length]);
    });
    return { paletteByCursoId: map, cursosOrdenados: ordenados };
  }, [colorearCursadasPorCurso, cursos, reunionesData]);

  const { year, month, firstDay, daysInMonth, startOffset } = useMemo(() => {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const firstDay = first.getDay();
    const daysInMonth = last.getDate();
    const startOffset = firstDay;
    return { year: y, month: m, firstDay, daysInMonth, startOffset };
  }, [viewDate]);

  const diasVista = useMemo(() => {
    const total = startOffset + daysInMonth;
    const celdas = Math.ceil(total / 7) * 7;
    const dias = [];
    for (let i = 0; i < startOffset; i++) dias.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      dias.push({ day: d, key, eventos: eventosPorFecha[key] || [] });
    }
    while (dias.length < celdas) dias.push(null);
    return dias;
  }, [year, month, daysInMonth, startOffset, eventosPorFecha]);

  const diasVistaPorMes = useMemo(() => {
    if (!vistaAnioCompleto) return null;
    const y = viewDate.getFullYear();
    return Array.from({ length: 12 }, (_, m) => {
      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);
      const startOffset = first.getDay();
      const daysInMonth = last.getDate();
      const dias = [];
      for (let i = 0; i < startOffset; i++) dias.push(null);
      for (let d = 1; d <= daysInMonth; d++) {
        const key = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        dias.push({ day: d, key, eventos: eventosPorFecha[key] || [] });
      }
      const total = startOffset + daysInMonth;
      const celdas = Math.ceil(total / 7) * 7;
      while (dias.length < celdas) dias.push(null);
      return dias;
    });
  }, [vistaAnioCompleto, viewDate, eventosPorFecha]);

  const prevMonth = () => setViewDate(new Date(year, month - 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1));
  const prevYear = () => setViewDate(new Date(viewDate.getFullYear() - 1, 0, 1));
  const nextYear = () => setViewDate(new Date(viewDate.getFullYear() + 1, 0, 1));

  const renderPanelEventos = () => {
    if (!tooltip) return null;
    const grupos = agruparEventosPorTipo(tooltip.eventos);
    return (
      <div className="calendario-tooltip-panel" aria-live="polite">
        <div className="calendario-tooltip-fecha">{formatFecha(tooltip.key)}</div>
        <div ref={panelEventosScrollRef} className="calendario-tooltip-eventos-scroll">
          {grupos.map((grupo) => (
          <div key={grupo.type} className={`calendario-tooltip-grupo calendario-tooltip-${grupo.type}`}>
            <div className="calendario-tooltip-grupo-titulo">{grupo.titulo}</div>
            <ul className="calendario-tooltip-grupo-lista">
              {grupo.items.map((ev, i) => (
                <li
                  key={i}
                  className="calendario-tooltip-grupo-item"
                  style={colorearCursadasPorCurso && ev.type === 'cursada' && ev.curso_id != null
                    ? { borderLeftColor: paletteByCursoId.get(ev.curso_id) || 'transparent' }
                    : undefined}
                >
                  {ev.type === 'cursada' && (
                    <>
                      <span className="calendario-tooltip-item-nombre">{ev.curso_nombre || 'Curso'}</span>
                      {ev.hora && (
                        <>
                          <span className="calendario-tooltip-item-sep"> — </span>
                          <span className="calendario-tooltip-hora">{ev.hora}</span>
                        </>
                      )}
                    </>
                  )}
                  {ev.type === 'publicacion_clase' && (
                    <>
                      <span className="calendario-tooltip-item-nombre">{ev.titulo}</span>
                      {ev.curso_nombre && (
                        <>
                          <span className="calendario-tooltip-item-sep"> — </span>
                          <span className="calendario-tooltip-item-detalle">{ev.curso_nombre}</span>
                        </>
                      )}
                      {ev.fecha_publicacion && (
                        <div className="calendario-tooltip-pub">Publicada: {formatFecha(ev.fecha_publicacion)}</div>
                      )}
                    </>
                  )}
                  {ev.type === 'tarea' && (
                    <>
                      <span className="calendario-tooltip-item-nombre">{ev.titulo}</span>
                      {ev.curso_nombre && (
                        <>
                          <span className="calendario-tooltip-item-sep"> — </span>
                          <span className="calendario-tooltip-item-detalle">{ev.curso_nombre}</span>
                        </>
                      )}
                      {ev.hora_cierre && (
                        <>
                          <span className="calendario-tooltip-item-sep"> — </span>
                          <span className="calendario-tooltip-hora">{ev.hora_cierre}</span>
                        </>
                      )}
                      {ev.created_at && (
                        <div className="calendario-tooltip-pub">Publicada: {formatFecha(ev.created_at)}</div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCell = (cell, idx, isAnioVista = false) => {
    if (!cell) {
      return <div key={`empty-${idx}`} className={`calendario-dia calendario-dia-vacio ${isAnioVista ? 'calendario-dia-mini' : ''}`} />;
    }
    const { day, key, eventos } = cell;
    const hasCursada = eventos.some((e) => e.type === 'cursada');
    const hasPublicacion = eventos.some((e) => e.type === 'publicacion_clase');
    const hasTarea = eventos.some((e) => e.type === 'tarea');
    const cursadaCursoIds = colorearCursadasPorCurso && hasCursada
      ? [...new Set(eventos.filter((e) => e.type === 'cursada' && e.curso_id != null).map((e) => e.curso_id))]
      : [];
    const usarColoresPorCurso = colorearCursadasPorCurso && cursadaCursoIds.length > 0;
    const primeraColor = usarColoresPorCurso ? paletteByCursoId.get(cursadaCursoIds[0]) : null;
    const classDia = [
      'calendario-dia',
      eventos.length ? 'calendario-dia-con-eventos' : '',
      hasCursada && !colorearCursadasPorCurso ? 'calendario-dia-cursada' : '',
      hasPublicacion ? 'calendario-dia-publicacion' : '',
      hasTarea ? 'calendario-dia-tarea' : '',
      usarColoresPorCurso ? 'calendario-dia-cursada-por-curso' : '',
      isAnioVista ? 'calendario-dia-mini' : '',
      onDayClick ? 'calendario-dia-clickeable' : '',
    ].filter(Boolean).join(' ');
    return (
      <div
        key={key}
        role={onDayClick ? 'button' : undefined}
        tabIndex={onDayClick ? 0 : undefined}
        className={classDia}
        style={usarColoresPorCurso && cursadaCursoIds.length === 1 ? { ['--curso-color']: primeraColor } : undefined}
        onClick={onDayClick ? () => onDayClick(key, eventos) : undefined}
        onKeyDown={onDayClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onDayClick(key, eventos); } : undefined}
        onMouseEnter={() => {
          if (eventos.length) setTooltip({ key, eventos });
        }}
      >
        <span className="calendario-dia-num">
          {day}
          {usarColoresPorCurso && cursadaCursoIds.length > 1 && (
            <span className="calendario-dia-dots">
              {cursadaCursoIds.map((cid) => (
                <span
                  key={cid}
                  className="calendario-dia-dot"
                  style={{ backgroundColor: paletteByCursoId.get(cid) }}
                />
              ))}
            </span>
          )}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="calendario calendario-loading">
        <div className="loading-spinner" />
        <p>Cargando calendario...</p>
      </div>
    );
  }

  if (vistaAnioCompleto && diasVistaPorMes) {
    const yearNav = viewDate.getFullYear();
    return (
      <div className={`calendario calendario-vista-anio ${vistaAnioCompleto ? 'calendario-es-anio' : ''}`}>
        <div className="calendario-header">
          <h3 className="calendario-titulo">{titulo}</h3>
          <div className="calendario-nav">
            <button type="button" className="calendario-nav-btn" onClick={prevYear} aria-label="Año anterior">
              ‹
            </button>
            <span className="calendario-mes">{yearNav}</span>
            <button type="button" className="calendario-nav-btn" onClick={nextYear} aria-label="Año siguiente">
              ›
            </button>
          </div>
        </div>
        {soloCursadas && colorearCursadasPorCurso && cursosOrdenados.length > 0 && (
          <div className="calendario-leyenda calendario-leyenda-por-curso">
            {cursosOrdenados.map((c) => (
              <span
                key={c.id}
                className="calendario-leyenda-item"
                style={{ ['--curso-color']: paletteByCursoId.get(c.id) }}
              >
                <span className="calendario-leyenda-dot" />
                {c.nombre || `Curso ${c.id}`}
              </span>
            ))}
          </div>
        )}
        <div
          className="calendario-cuerpo"
          ref={cuerpoRef}
          onMouseLeave={() => setTooltip(null)}
        >
          <div className="calendario-anio-grid">
            {diasVistaPorMes.map((diasMes, m) => (
              <div key={m} className="calendario-mes-block">
                <div className="calendario-mes-block-titulo">{MESES_CORTOS[m]}</div>
                <div className="calendario-semana-headers calendario-semana-headers-mini">
                  {DIA_SEMANA.map((d) => (
                    <div key={d} className="calendario-dia-header">{d}</div>
                  ))}
                </div>
                <div className="calendario-grid calendario-grid-mini">
                  {diasMes.map((cell, idx) => renderCell(cell, idx, true))}
                </div>
              </div>
            ))}
          </div>
          {renderPanelEventos()}
        </div>
      </div>
    );
  }

  return (
    <div className="calendario">
      <div className="calendario-header">
        <h3 className="calendario-titulo">{titulo}</h3>
        <div className="calendario-nav">
          <button type="button" className="calendario-nav-btn" onClick={prevMonth} aria-label="Mes anterior">
            ‹
          </button>
          <span className="calendario-mes">{MESES[month]} {year}</span>
          <button type="button" className="calendario-nav-btn" onClick={nextMonth} aria-label="Mes siguiente">
            ›
          </button>
        </div>
      </div>
      {!soloCursadas && (
        <div className="calendario-leyenda">
          <span className="calendario-leyenda-item calendario-leyenda-cursada">Cursada (reunión)</span>
          <span className="calendario-leyenda-item calendario-leyenda-publicacion">Publicación clase</span>
          <span className="calendario-leyenda-item calendario-leyenda-tarea">Tareas</span>
        </div>
      )}
      {soloCursadas && !colorearCursadasPorCurso && (
        <div className="calendario-leyenda">
          <span className="calendario-leyenda-item calendario-leyenda-cursada">Cursada / Reunión</span>
        </div>
      )}
      {soloCursadas && colorearCursadasPorCurso && cursosOrdenados.length > 0 && (
        <div className="calendario-leyenda calendario-leyenda-por-curso">
          {cursosOrdenados.map((c) => (
            <span
              key={c.id}
              className="calendario-leyenda-item"
              style={{ ['--curso-color']: paletteByCursoId.get(c.id) }}
            >
              <span className="calendario-leyenda-dot" />
              {c.nombre || `Curso ${c.id}`}
            </span>
          ))}
        </div>
      )}
      <div
        className="calendario-cuerpo"
        ref={cuerpoRef}
        onMouseLeave={() => setTooltip(null)}
      >
        <div className="calendario-semana-headers">
          {DIA_SEMANA.map((d) => (
            <div key={d} className="calendario-dia-header">{d}</div>
          ))}
        </div>
        <div className="calendario-grid">
          {diasVista.map((cell, idx) => renderCell(cell, idx, false))}
        </div>
        {renderPanelEventos()}
      </div>
    </div>
  );
};

export default Calendario;
